<template>
  <page-template :is-loading="isLoading" :data-exists="selectedLogExporterData !== null">
    <template #pageHeadline>
      {{
        isNew
          ? $t('logExporter.configPage.newPageHeadline')
          : $t('logExporter.configPage.editPageHeadline')
      }}
    </template>
    <template #secondLevelHeaderLeft>
      <rbz-form-button
        variant="tertiary"
        icon="IconArrowBack"
        v-b-tooltip="$t('common.operations.returnToList')"
        link-route-name="Log Exporters List"
        classes="redirect-to-list-button"
        data-qa="redirect-to-list-button"
      />
      <template v-if="selectedLogExporter && !isNew">
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
        <rbz-dropdown
          :items="dropdownData.map((item) => [item.id, item.name])"
          :disabled="isLoading"
          v-model:value="selectedLogExporter"
          @update:value="switchLogExporter($event)"
          :ignore-update-in-process="true"
          type="single-select"
          :header-tooltip="$t('logExporter.configPage.switchTooltip')"
          classes="switch-log-exporter switch-page-entry"
          data-qa="switch-log-exporter"
        />
      </template>
    </template>
    <template #secondLevelHeaderRight>
      <template v-if="selectedLogExporter && !isNew">
        <rbz-form-button
          :label="$t('common.operations.download.label')"
          variant="tertiary"
          icon="IconDownload"
          @button-click="downloadLogExporter()"
          class="download-button"
          :disabled="isLoading"
          data-qa="download-button"
        />
        <rbz-form-button
          :label="$t('common.operations.delete.label')"
          variant="tertiary"
          icon="IconDelete"
          @button-click="openDeleteLogExporterModal()"
          class="delete-button"
          :disabled="isLoading"
          data-qa="delete-button"
        />
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
      </template>
      <rbz-form-button
        :label="$t('common.operations.save.label')"
        :disabled="isLoading || !isLogExporterValid"
        @button-click="saveChanges()"
        class="save-changes-button"
        data-qa="save-changes-button"
      />
    </template>
    <template #pageContent>
      <div class="notice-wrapper">
        <system-notice color="fun-blue" :text="$t('logExporter.configPage.saveNoticeText')" />
      </div>
      <div class="rbz-columns">
        <div class="rbz-column is-4">
          <div class="rbz-field">
            <rbz-form-input
              name="name"
              classes="name"
              data-qa="name-input"
              :label="$t('logExporter.configPage.name')"
              :placeholder="$t('logExporter.configPage.uniqueLogExporter')"
              v-model:inputValue="selectedLogExporterData.name"
            />
          </div>
          <div class="rbz-field">
            <label class="rbz-label">
              {{ $t('logExporter.configPage.status') }}
            </label>
            <rbz-form-toggle
              v-model:buttonValue="selectedLogExporterData.active"
              :label="$t('logExporter.configPage.active')"
              class="active-toggle"
              data-qa="active-toggle"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-input
              name="connection-host"
              classes="connection-host"
              data-qa="connection-host-input"
              :label="$t('logExporter.configPage.destinationIp')"
              :placeholder="$t('logExporter.configPage.destinationIp')"
              v-model:inputValue="selectedLogExporterData.connection.host"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-input
              name="connection-port"
              classes="connection-port"
              data-qa="connection-port-input"
              type="number"
              :label="$t('logExporter.configPage.port')"
              :placeholder="$t('logExporter.configPage.port')"
              v-model:inputValue="selectedLogExporterData.connection.port"
            />
          </div>
          <div class="rbz-field">
            <label class="rbz-label">
              {{ $t('logExporter.configPage.serverGroups') }}
            </label>
            <div class="server-groups-radio-wrapper">
              <rbz-form-radio
                id="server-groups-radio-all"
                name="server-groups-radio"
                data-qa="server-groups-radio-all"
                :label="$t('logExporter.configPage.serverGroupsAll')"
                @input="selectedLogExporterData['server_groups'].all = true"
                :checked="selectedLogExporterData['server_groups'].all"
              />
              <rbz-form-radio
                id="server-groups-radio-selected"
                name="server-groups-radio"
                data-qa="server-groups-radio-selected"
                :label="$t('logExporter.configPage.serverGroupsSelected')"
                @input="selectedLogExporterData['server_groups'].all = false"
                :checked="!selectedLogExporterData['server_groups'].all"
              />
            </div>
          </div>
          <div class="rbz-field">
            <rbz-dropdown
              v-if="!selectedLogExporterData['server_groups'].all"
              :items="serverGroupsNames"
              :placeholder="$t('logExporter.configPage.serverGroupsSelected')"
              type="multi-select"
              v-model:value="selectedLogExporterData['server_groups'].ids"
              classes="server-groups-selection"
              data-qa="server-groups-dropdown"
              :label="$t('logExporter.configPage.serverGroups')"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-toggle
              v-model:buttonValue="selectedLogExporterData.include_request_data"
              :label="$t('logExporter.configPage.includeRequestData')"
              class="include-request-data-toggle"
              data-qa="include-request-data-toggle"
            />
          </div>
        </div>
        <div class="rbz-column is-8">
          <div class="rbz-columns">
            <div class="rbz-column is-5">
              <div class="rbz-field">
                <rbz-dropdown
                  :items="transportProtocolItems"
                  v-model:value="selectedLogExporterData.connection['tls_mode']"
                  classes="transport-protocol-selection"
                  data-qa="transport-protocol-dropdown"
                  :label="$t('logExporter.configPage.transportProtocol')"
                />
              </div>
              <div class="rbz-field">
                <label class="rbz-label">
                  {{ $t('logExporter.configPage.requestExport') }}
                </label>
                <div class="request-export-radio-wrapper">
                  <rbz-form-radio
                    id="request-export-blocked"
                    name="request-export-radio"
                    data-qa="request-export-radio-blocked"
                    :label="$t('logExporter.configPage.requestExportBlocked')"
                    @input="selectedLogExporterData.filter = 'blocked'"
                    :checked="selectedLogExporterData.filter === 'blocked'"
                  />
                  <rbz-form-radio
                    id="request-export-all"
                    name="request-export-radio"
                    data-qa="request-export-radio-all"
                    :label="$t('logExporter.configPage.requestExportAll')"
                    @input="selectedLogExporterData.filter = 'all'"
                    :checked="selectedLogExporterData.filter === 'all'"
                  />
                </div>
              </div>
            </div>
            <div v-if="selectedLogExporterData.connection['tls_mode'] === 'tls'" class="rbz-column">
              <div class="rbz-field">
                <label class="rbz-label">
                  {{ $t('logExporter.configPage.uploadCertificate') }}
                </label>
                <div class="file has-name">
                  <label class="file-label">
                    <input
                      class="file-input"
                      type="file"
                      @input="loadFile($event)"
                      name="certFileInput"
                      data-qa="file-input"
                      accept=".pem"
                    />
                    <span class="file-cta">
                      <span class="file-icon">
                        <icon-upload />
                      </span>
                      <span class="file-label">
                        {{ $t('logExporter.configPage.chooseFile') }}
                      </span>
                    </span>
                    <span class="file-name">
                      {{ certFileName || $t('common.noFileChosen') }}
                    </span>
                  </label>
                </div>
              </div>
              <div class="rbz-field">
                <rbz-form-button
                  v-if="selectedLogExporterData.connection.certificate.chain_data.length > 0"
                  :label="$t('logExporter.configPage.copyCertificate')"
                  variant="tertiary"
                  icon="IconDuplicate"
                  @button-click="copyCertificateBodyToClipboard()"
                  class="copy-certificate"
                  data-qa="copy-certificate"
                />
              </div>
              <div v-for="(item, index) in mapChainData" :key="item.certName" class="rbz-field">
                <div class="chain-data-wrapper" :class="{ alert: item.expired }">
                  <p>
                    <span class="chain-data-key">
                      {{ `${$t('logExporter.configPage.certificateName')} ${index + 1}: ` }}
                    </span>
                    <span class="chain-data-value">
                      {{ item.certName }}
                    </span>
                  </p>
                  <p>
                    <span :class="{ expired: item.expired }" class="chain-data-key">
                      {{ `${$t('logExporter.configPage.certificateExpires')}: ` }}
                    </span>
                    <span :class="{ expired: item.expired }" class="chain-data-value">
                      {{ item.expDate }}
                    </span>
                  </p>
                  <p v-if="item.expired">
                    <span class="expired"> has expired </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <rbz-modal-dialog
        :show="showDeleteLogExporterModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteLogExporter"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteLogExporterModal = false"
        @closeModal="showDeleteLogExporterModal = false"
        size="medium"
      >
        <template #content>
          {{ $t('common.operations.delete.modal.message', { name: selectedLogExporterData.name }) }}
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>

<script lang="ts">
import _ from 'lodash';
import { defineComponent } from 'vue';
import { useRouting } from '@/helpers/RoutingUtils';
import {
  LogExporter,
  BasicDocument,
  DocumentName,
  ServerGroup,
  ChainData,
  GenericObject,
} from '@/types';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import RequestsUtils from '@/helpers/RequestsUtils';
import Utils from 'ui-component-library/helpers/Utils';
import RbzFormButton from '@/components/RbzFormButton.vue';
import RbzDropdown from '@/components/RbzDropdown.vue';
import RbzFormInput from '@/components/RbzFormInput.vue';
import PageTemplate from '@/views/PageTemplate.vue';
import LayoutDividerLine from 'ui-component-library/rbz/components/LayoutDividerLine.vue';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import RbzFormToggle from '@/components/RbzFormToggle.vue';
import RbzFormRadio from '@/components/RbzFormRadio.vue';
import SystemNotice from 'ui-component-library/rbz/components/SystemNotice.vue';
import IconUpload from 'ui-component-library/assets/icons/IconUpload.vue';

export default defineComponent({
  name: 'LogExportersEditor',
  setup() {
    const { getRouteName, getListPath, navigateTo, normalizeRouteName } = useRouting();
    return { getRouteName, getListPath, navigateTo, normalizeRouteName };
  },
  components: {
    IconUpload,
    SystemNotice,
    RbzFormRadio,
    RbzFormToggle,
    RbzFormButton,
    RbzFormInput,
    LayoutDividerLine,
    PageTemplate,
    RbzDropdown,
    RbzModalDialog,
  },
  data() {
    return {
      dropdownData: [] as BasicDocument[],
      isNew: false,
      selectedLogExporter: null as string,
      selectedLogExporterData: null as LogExporter,
      originalLogExporterData: null as LogExporter,
      pageLoadingCounter: null,
      isDeleteLoading: false,
      certFileName: null as string,
      showDeleteLogExporterModal: false,
      serverGroupsNames: [] as [ServerGroup['id'], ServerGroup['name']][],
      transportProtocolItems: [
        ['no_tls', this.$t('logExporter.configPage.transportProtocolDropdown.tcp')],
        [
          'tls_no_verify',
          this.$t('logExporter.configPage.transportProtocolDropdown.tcpAndTlsUntrusted'),
        ],
        ['tls', this.$t('logExporter.configPage.transportProtocolDropdown.tcpAndTlsTrusted')],
      ],
      certBodyOld: null as string,
    };
  },
  computed: {
    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    selectedBranch(): string {
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useUserStore),

    entityName(): DocumentName {
      return {
        singular: this.$t('logExporter.singular'),
        plural: this.$t('logExporter.plural'),
      };
    },

    isLoading(): boolean {
      return (
        this.pageLoadingCounter === null || this.pageLoadingCounter > 0 || this.isDeleteLoading
      );
    },

    entityNotSaved(): boolean {
      return !_.isEqual(this.selectedLogExporterData, this.originalLogExporterData);
    },

    isLogExporterValid(): boolean {
      const trimmedName = this.selectedLogExporterData.name.toLowerCase().trim();
      const nameExists = this.dropdownData.filter((item) => {
        return item.name.toLowerCase().trim() === trimmedName;
      }).length;
      return (
        this.selectedLogExporterData.name &&
        (trimmedName === this.originalLogExporterData.name.toLowerCase().trim() || !nameExists)
      );
    },

    mapChainData(): GenericObject[] {
      const chainData = [] as GenericObject[];
      this.selectedLogExporterData.connection.certificate.chain_data.forEach((item: ChainData) => {
        let certName = _.split(item.cn, ', ', 6)[5];
        if (certName) {
          certName = _.split(certName, '=')[1];
        } else {
          certName = this.$t('common.noDataMessage');
        }
        let expired = false;
        const now = Date.now();
        if (new Date(item.exp_date).getTime() < now) {
          expired = true;
        }
        const expDate = _.split(item.exp_date, '-');
        chainData.push({
          certName: certName,
          expDate: expDate[2] + '.' + expDate[1] + '.' + expDate[0],
          expired: expired,
        });
      });
      return chainData;
    },
  },
  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    redirectToList() {
      this.navigateTo(this.getListPath('log-exporters'));
    },

    switchLogExporter(logExporterId: string) {
      this.$router.push(`/${this.selectedBranch}/log-exporters/config/${logExporterId}`);
    },

    async loadDropdownData() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/log-exporters`,
        config: { headers: { 'x-fields': 'id, name' } },
        displayServerFailureMessage: true,
      });
      this.dropdownData = Utils.sortArrayByName(response?.data?.items) as BasicDocument[];
      this.setPageLoadingStatus(false);
    },

    newLogExporter(): LogExporter {
      const factory = DatasetsUtils.newEntityFactory['log-exporter'];
      return factory && factory();
    },

    async loadLogExporter(logExporterId: string) {
      this.setPageLoadingStatus(true);
      this.selectedLogExporter = logExporterId;
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/log-exporters/${logExporterId}`,
      });
      this.selectedLogExporterData = response?.data || {};
      if (!this.selectedLogExporterData.connection?.certificate) {
        this.selectedLogExporterData.connection['certificate'] = {
          chain_data: [],
          cert_body: '',
          expired: false,
        };
      }
      this.originalLogExporterData = _.cloneDeep(this.selectedLogExporterData);
      this.setPageLoadingStatus(false);
    },

    copyCertificateBodyToClipboard() {
      navigator.clipboard.writeText(
        this.certBodyOld || this.selectedLogExporterData.connection.certificate.cert_body
      );
    },

    downloadLogExporter() {
      Utils.downloadFile('log-exporter', 'json', this.selectedLogExporterData);
    },

    openDeleteLogExporterModal() {
      this.showDeleteLogExporterModal = true;
    },

    async deleteLogExporter() {
      this.isDeleteLoading = true;
      const logExporterId = this.selectedLogExporter;
      const dropdownIndex = _.findIndex(this.dropdownData, (item) => {
        return item.id === logExporterId;
      });
      this.dropdownData.splice(dropdownIndex, 1);
      this.selectedLogExporter = null;
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: `conf/${this.selectedBranch}/log-exporters/${logExporterId}`,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.redirectToList();
      }
      this.isDeleteLoading = false;
    },

    async saveChanges() {
      this.setPageLoadingStatus(true);
      let successMessage;
      let failureMessage;
      if (this.isNew) {
        successMessage = this.$t('common.operations.new.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.new.messages.failure', this.entityName);
      } else {
        successMessage = this.$t('common.operations.save.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.save.messages.failure', this.entityName);
      }
      if (this.selectedLogExporterData.server_groups.ids.length === 0) {
        this.selectedLogExporterData.server_groups.all = true;
      }
      this.selectedLogExporterData.name = this.selectedLogExporterData.name.trim();
      const logExporterToSave = _.cloneDeep(this.selectedLogExporterData);
      delete logExporterToSave.connection.certificate['chain_data'];
      delete logExporterToSave.connection.certificate['expired'];
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: this.isNew ? 'POST' : 'PUT',
        url: `conf/${this.selectedBranch}/log-exporters/${this.selectedLogExporterData.id}`,
        data: logExporterToSave,
        successMessage,
        failureMessage,
        displayServerFailureMessage: true,
      });
      if (response) {
        if (this.isNew) {
          const currentRoute = this.$route.path;
          const targetRoute = currentRoute.replace(
            'new',
            `config/${this.selectedLogExporterData.id}`
          );
          this.$router.push(targetRoute);
        } else {
          await this.loadLogExporter(this.selectedLogExporterData.id);
          await this.loadDropdownData();
          this.originalLogExporterData = _.cloneDeep(this.selectedLogExporterData);
        }
        this.certBodyOld = null;
      } else {
        this.selectedLogExporterData.connection.certificate.cert_body = this.certBodyOld;
      }
      this.certFileName = null;
      this.setPageLoadingStatus(false);
    },

    async loadServerGroups() {
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/server-groups`,
        config: { headers: { 'x-fields': 'id, name' } },
      });
      this.serverGroupsNames = _.sortBy(
        _.map(response?.data?.items, (entity) => {
          return [entity.id, entity.name];
        }),
        (e) => {
          return e[1];
        }
      );
    },

    async loadFile(event: Event) {
      const target = event.target as HTMLInputElement;
      const file: File = target.files[0];
      const extension = file?.name?.split('.')?.pop();
      if (extension?.toLowerCase() === 'pem') {
        this.certFileName = file.name;
        const certBodyFile = await file.text();
        this.certBodyOld = this.selectedLogExporterData.connection.certificate.cert_body;
        this.selectedLogExporterData.connection.certificate.cert_body =
          this.selectedLogExporterData.connection.certificate.cert_body + '\n' + certBodyFile;
      } else {
        this.certFileName = null;
        target.value = null;
      }
    },

    setSessionDataWatcher() {
      this.$watch(
        () => [this.selectedLogExporterData, this.entityNotSaved],
        (params) => {
          const [formData, entityNotSaved] = params;
          Utils.saveSessionData(formData, entityNotSaved);
        },
        {
          deep: true,
          immediate: true,
        }
      );
    },
  },
  created() {
    this.$watch(
      () => [this.$route.params?.['branch'], this.$route.params?.['doc_id']],
      async (routeParams) => {
        const [_newBranch, newId] = routeParams;
        const routeName: string = this.normalizeRouteName(this.$route.name as string);
        if (['New Log Exporter', 'Log Exporters Editor'].includes(routeName)) {
          this.setPageLoadingStatus(true);
          this.isNew = routeName.startsWith('New');
          await this.branchesStore.getBranches();
          const dropdownDataPromise = this.loadDropdownData();
          const serverGroupsPromise = this.loadServerGroups();
          await Promise.all([dropdownDataPromise, serverGroupsPromise]);
          if (this.isNew) {
            this.selectedLogExporterData = this.newLogExporter();
            this.selectedLogExporterData.name = Utils.generateUniqueEntityName(
              this.selectedLogExporterData.name,
              _.map(this.dropdownData, 'name')
            );
            this.originalLogExporterData = _.cloneDeep(this.selectedLogExporterData);
          } else {
            const logExporterId = newId as string;
            const dataExists = this.dropdownData.some((item) => {
              return item.id === logExporterId;
            });
            if (dataExists) {
              await this.loadLogExporter(logExporterId);
            } else {
              this.redirectToList();
              return;
            }
          }
          const savedSessionData = sessionStorage.getItem('formData');
          if (savedSessionData) {
            this.selectedLogExporterData = JSON.parse(savedSessionData);
          }
          this.setSessionDataWatcher();
          this.setPageLoadingStatus(false);
        }
      },
      {
        immediate: true,
      }
    );
  },
  beforeRouteLeave() {
    Utils.clearSessionData();
  },
});
</script>
<style scoped lang="scss">
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'sass:map';

.server-groups-radio-wrapper,
.request-export-radio-wrapper {
  display: flex;
  gap: 16px;
}

.chain-data-wrapper {
  align-items: flex-start;
  align-self: stretch;
  background-color: map.get(colors.$colors, 'wild-sand');
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 8px 16px;

  .chain-data-key {
    color: map.get(colors.$colors, 'fun-blue');
    font-size: 16px;
  }
}

.chain-data-wrapper.alert {
  border: 1px solid map.get(colors.$colors, 'crimson');
  background-color: map.get(colors.$colors, 'linen');
}

.expired {
  color: map.get(colors.$colors, 'crimson');
}

.notice-wrapper {
  margin-bottom: 32px;
}
</style>
